import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { createScene } from './three/scene.js';
import { CHAPTER_WEIGHTS, chapterHeightVh } from './story-config.js';
import { initMotionInteractions } from './motion-interactions.js';

gsap.registerPlugin(ScrollTrigger);

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------
   1. Smooth scroll (Lenis) driving GSAP's ticker — the ONLY render/
   scroll loop on the page. No second Lenis instance, no second ticker.
   ------------------------------------------------------------------ */
const lenis = new Lenis({
  duration: 1.15,
  easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
  smoothWheel: true,
  wheelMultiplier: 1,
  touchMultiplier: 1.1,
});

lenis.on('scroll', ScrollTrigger.update);

gsap.ticker.add((time) => {
  lenis.raf(time * 1000);
});
gsap.ticker.lagSmoothing(0);

/* ------------------------------------------------------------------
   2. Story section heights — set here, ONCE, from story-config.js.
   To change the total scroll length of the assembly story, edit
   STORY_VH in src/story-config.js (nothing else needs to change).
   ------------------------------------------------------------------ */
Object.keys(CHAPTER_WEIGHTS).forEach((key) => {
  const el = document.querySelector(`[data-chapter="${key}"]`);
  if (el) el.style.minHeight = `${chapterHeightVh(key)}vh`;
});

/* ------------------------------------------------------------------
   3. Three.js stage — fixed, viewport-filling canvas shared by the
   whole site (see .scene-canvas in main.css).
   ------------------------------------------------------------------ */
const canvas = document.getElementById('scene');
const sceneApi = createScene(canvas);

function renderLoop() {
  sceneApi.render();
  requestAnimationFrame(renderLoop);
}
renderLoop();

let resizeRaf = null;
window.addEventListener('resize', () => {
  sceneApi.resize();
  if (resizeRaf) cancelAnimationFrame(resizeRaf);
  resizeRaf = requestAnimationFrame(() => ScrollTrigger.refresh());
});

/* ------------------------------------------------------------------
   4. THE central timeline — single GSAP timeline, scrub: 1, spanning
   #story top->bottom. Its progress (0..1) is the one and only driver
   of the garment assembly, camera and lighting. Everything else on
   the page (reveals, product info, gallery) is ordinary scroll
   content that happens to live around it.
   ------------------------------------------------------------------ */
const storyEl = document.getElementById('story');
const storyState = { progress: 0 };

const storyTimeline = gsap.timeline({
  scrollTrigger: {
    trigger: storyEl,
    start: 'top top',
    end: 'bottom bottom',
    scrub: 1,
  },
});

storyTimeline.to(storyState, {
  progress: 1,
  ease: 'none',
  onUpdate: () => sceneApi.setStoryProgress(storyState.progress),
});

/* ------------------------------------------------------------------
   5. Global progress rail (whole document, not just the story).
   ------------------------------------------------------------------ */
const progressFill = document.getElementById('progressFill');
ScrollTrigger.create({
  trigger: document.body,
  start: 'top top',
  end: 'bottom bottom',
  onUpdate: (self) => {
    progressFill.style.width = `${self.progress * 100}%`;
  },
});

/* ------------------------------------------------------------------
   6. Canvas fade — the garment stays the hero for the whole story,
   then fades out as the reading-focused sections below take over.
   ------------------------------------------------------------------ */
gsap.to(canvas, {
  opacity: 0,
  ease: 'none',
  scrollTrigger: {
    trigger: '#story-note',
    start: 'top bottom',
    end: 'top 20%',
    scrub: true,
  },
});

/* ------------------------------------------------------------------
   6b. Brand-reveal wordmark — big cinematic "CARLO COLUCCI" blends in
   behind the finished garment as the product-hero chapter settles:
   blur-to-sharp, scale down, and a slow shimmer across the gradient
   text-fill. Entirely scroll-driven (scrub), like everything else.
   ------------------------------------------------------------------ */
const brandReveal = document.getElementById('brandReveal');
if (brandReveal) {
  gsap.timeline({
    scrollTrigger: {
      trigger: '#chapter-product',
      start: 'top 70%',
      end: 'bottom bottom',
      scrub: 0.6,
    },
  })
    .fromTo(
      brandReveal,
      { opacity: 0, filter: 'blur(24px)', scale: 1.3, backgroundPosition: '0% 0%' },
      { opacity: 0.16, filter: 'blur(0px)', scale: 1, backgroundPosition: '100% 0%', ease: 'power2.out' }
    );
}

/* ------------------------------------------------------------------
   7. Generic reveal-on-scroll for anything tagged [data-reveal]
   ------------------------------------------------------------------ */
document.querySelectorAll('[data-reveal]').forEach((el) => {
  gsap.to(el, {
    opacity: 1,
    y: 0,
    duration: 1,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: el,
      start: 'top 85%',
      toggleActions: 'play none none reverse',
    },
  });
});

/* ------------------------------------------------------------------
   8. Split-line headlines for [data-split] — line-by-line reveal
   ------------------------------------------------------------------ */
document.querySelectorAll('[data-split]').forEach((el) => {
  const originalHTML = el.innerHTML;
  const lines = originalHTML.split('<br>');
  el.innerHTML = lines
    .map(
      (line) =>
        `<span class="split-line-wrap" style="display:block;overflow:hidden;"><span class="split-line" style="display:block;">${line}</span></span>`
    )
    .join('');

  gsap.to(el.querySelectorAll('.split-line'), {
    opacity: 1,
    y: '0%',
    duration: 1.1,
    stagger: 0.12,
    ease: 'power3.out',
    scrollTrigger: {
      trigger: el,
      start: 'top 80%',
      toggleActions: 'play none none reverse',
    },
  });
});

/* ------------------------------------------------------------------
   9. Hero entrance timeline (page load, not scroll-driven)
   ------------------------------------------------------------------ */
const heroTl = gsap.timeline({ delay: 0.3 });
heroTl.fromTo(
  '.chapter--hero [data-reveal]',
  { opacity: 0, y: 34, scale: 1.04, filter: 'blur(6px)' },
  {
    opacity: 1,
    y: 0,
    scale: 1,
    filter: 'blur(0px)',
    duration: 1.3,
    stagger: 0.12,
    ease: 'power3.out',
});

/* ------------------------------------------------------------------
   10. Size selector — simple active-state toggle
   ------------------------------------------------------------------ */
document.querySelectorAll('.size-selector__btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    document
      .querySelectorAll('.size-selector__btn')
      .forEach((b) => b.classList.remove('size-selector__btn--active'));
    btn.classList.add('size-selector__btn--active');
  });
});

/* ------------------------------------------------------------------
   11. Preloader — waits for BOTH window load and the glTF/textures to
   finish, whichever is later, then fades out. Shows load progress.
   ------------------------------------------------------------------ */
const preloader = document.getElementById('preloader');
const preloaderFill = document.getElementById('preloaderFill');

let windowLoaded = false;
let modelLoaded = false;

function maybeHidePreloader() {
  if (!windowLoaded || !modelLoaded) return;
  gsap.to(preloader, {
    opacity: 0,
    duration: 0.6,
    delay: 0.2,
    ease: 'power2.out',
    onComplete: () => preloader.remove(),
  });
}

window.addEventListener('load', () => {
  windowLoaded = true;
  maybeHidePreloader();
});

sceneApi.whenLoaded(() => {
  modelLoaded = true;
  if (preloaderFill) preloaderFill.style.width = '100%';
  maybeHidePreloader();
  // The glTF (10MB+) can finish loading after ScrollTrigger has already
  // measured the page — recalculate once it's actually in so pin/scrub
  // distances stay accurate.
  ScrollTrigger.refresh();
});

/* ------------------------------------------------------------------
   12. Respect reduced motion — show the finished garment statically,
   reveal all content immediately, skip every scroll-linked scrub.
   ------------------------------------------------------------------ */
if (reducedMotion) {
  ScrollTrigger.getAll().forEach((st) => st.kill());
  storyTimeline.kill();
  sceneApi.setStoryProgress(1);
  gsap.set(canvas, { opacity: 1 });
  document.querySelectorAll('[data-reveal], .split-line').forEach((el) => {
    gsap.set(el, { opacity: 1, y: 0 });
  });
}

/* ------------------------------------------------------------------
   13. Motion (motion.dev) — premium pointer micro-interactions: custom
   cursor, magnetic buttons, spring press/hover, sliding size pill.
   Fully independent of the GSAP/ScrollTrigger story engine above.
   ------------------------------------------------------------------ */
initMotionInteractions();
